import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AnatomicalSideEnum, convertToLocalDate, EMAIL_REGEX, PublishDateFilterType, TreatmentStatusType, TspBoneTypeEnum } from '../../core';
import { FiltersForm } from '../../models';
import { ListService, UserService } from '../../services';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe, DateFilterLabelPipe, DatepickerComponent, PlanStatusLabelPipe, ReplacePipe } from '../../shared';


/**
 * Component for filters of Patient List.
 */
@Component({
	selector: 'tsp-filters',
	imports: [
		ReplacePipe,
		BoneTypeLabelPipe,
		DatepickerComponent,
		ReactiveFormsModule,
		PlanStatusLabelPipe,
		DateFilterLabelPipe,
		AnatomicalSideLabelPipe
	],
	templateUrl: './filters.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class FiltersComponent extends BaseComponent {

	private readonly listSrv = inject(ListService);
	private readonly usrSrv = inject(UserService);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);

	isCustomerCare = toSignal(this.usrSrv.isCustomerCare());
	publishDate = signal<Date | null>(null);
	publishDateFormat = computed(() => this.publishDate() ? `${convertToLocalDate(this.publishDate()!, false)}` : '');

	readonly USERNAME_MAXLENGTH = 128;
	readonly ID_MAXLENGTH = 50;
	readonly FRAMEID_REGEX = /^[A-F]$/;

	readonly statusList = [TreatmentStatusType.PUBLISHED, TreatmentStatusType.REVOKED];
	readonly sideList = [AnatomicalSideEnum.Left, AnatomicalSideEnum.Right];
	readonly boneTypeList: TspBoneTypeEnum[] = [TspBoneTypeEnum.LongLeg, TspBoneTypeEnum.Femur, TspBoneTypeEnum.Tibia, TspBoneTypeEnum.Ankle, TspBoneTypeEnum.Forefoot, TspBoneTypeEnum.Hindfoot];
	readonly dateFilterList = [PublishDateFilterType.THISDATE, PublishDateFilterType.AFTERDATE, PublishDateFilterType.BEFOREDATE];

	filtersForm = new FormGroup({
		userName: new FormControl(null, [Validators.pattern(EMAIL_REGEX), Validators.maxLength(this.USERNAME_MAXLENGTH)]),
		patientId: new FormControl(null, [Validators.maxLength(this.ID_MAXLENGTH)]),
		planId: new FormControl(null, [Validators.maxLength(this.ID_MAXLENGTH)]),
		frameId: new FormControl(null, [Validators.pattern(this.FRAMEID_REGEX)]),
		status: new FormControl(null),
		side: new FormControl(null),
		boneType: new FormControl(null),
		dateFilter: new FormControl(PublishDateFilterType.THISDATE)
	});


	get userName() {
		return this.filtersForm.get('userName')!;
	}

	get patientId() {
		return this.filtersForm.get('patientId')!;
	}

	get planId() {
		return this.filtersForm.get('planId')!;
	}

	get frameId() {
		return this.filtersForm.get('frameId')!;
	}

	get status() {
		return this.filtersForm.get('status')!;
	}

	get side() {
		return this.filtersForm.get('side')!;
	}

	get boneType() {
		return this.filtersForm.get('boneType')!;
	}

	get dateFilter() {
		return this.filtersForm.get('dateFilter')!;
	}

	/**
	 * Clear filters.
	 */
	clear(): void {
		this.filtersForm.reset({ dateFilter: PublishDateFilterType.THISDATE });
		this.publishDate.set(null);
		this._search(this.filtersForm.value);
	}

	/**
	 * Apply search filters.
	 */
	search(): void {
		const formValue: Partial<FiltersForm> = this.filtersForm.value;
		if (formValue.userName) {
			this.usrSrv.getUserGuid(formValue.userName).subscribe(guid => {
				if (guid) {
					formValue.userGuid = guid;
					this._search(formValue);
				} else {
					this.userName.setErrors({ userNameNotExists: true });
					this.changeDetectorRef.markForCheck();
				}
			});
		} else {
			this._search(formValue);
		}
	}

	private _search(value: Partial<FiltersForm>): void {
		value.publishDate = this.publishDate();
		this.listSrv.applyFilters(value);
		this.listSrv.search();
	}

}
